<template>
  <div>
  <Hero />
  <Breadcrumb />

  <!-- Desktop -->
  <div class="w-full mx-4 hidden md:block">
    <div class="grid grid-cols-12 gap-4 p-1">
      <!-- Sidebar Section -->
      <aside class="col-span-2 bg-theme-bg2 rounded p-2">
        <Filter @filter-applied="onFilterApplied" />
      </aside>

      <!-- Main Content Section -->
      <main class="col-span-10 bg-theme-bg2 p-1">
        
        <!-- 🌟 Featured Items Carousel -->
        <section 
          v-if="featuredEquipments.length > 0" 
          class="featured-equipments mb-6 py-6 bg-theme-bg2 border border-theme-border rounded-lg shadow-theme-card relative"
        >
          <h2 class="text-2xl font-extrabold text-secondary-500 mb-4 text-center flex items-center justify-center">
            <i class="pi pi-star-fill text-secondary-500 mr-2"></i> Featured Items
          </h2>

          <div class="relative">
            <div class="overflow-hidden">
              <div 
                class="flex transition-transform duration-700 ease-in-out"
                :style="{ transform: `translateX(-${currentFeaturedIndex * 100}%)` }"
              >
                <div 
                  v-for="item in featuredEquipments" 
                  :key="item.id" 
                  class="flex-shrink-0 w-1/2 sm:w-1/2 md:w-1/3 lg:w-1/4 px-3"
                >
                  <div 
                    class="p-2 rounded-xl bg-theme-card border border-theme-border shadow-theme-card hover:shadow-theme-modal transition cursor-pointer"
                    @click="goToDetail(item.id)"
                  >
                    <EquipmentCard :item="item" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Prev Button -->
            <button @click="prevFeatured" class="carousel-button left-2">
              <i class="pi pi-chevron-left text-xl"></i>
            </button>

            <!-- Next Button -->
            <button @click="nextFeatured" class="carousel-button right-2">
              <i class="pi pi-chevron-right text-xl"></i>
            </button>
          </div>
        </section>

        <!-- Default Items -->
        <Card />
      </main>
    </div>
  </div>

  <!-- Mobile -->
  <div class="px-1 py-2 w-full text-xs md:hidden">
    <MobileFilter 
      :categories="categories" 
      :cities="store.cities" 
      :selectedCategories="selectedCategories"
      :selectedCities="selectedCities"
      @update:selectedCategories="updateSelectedCategories"
      @update:selectedCities="updateSelectedCities"
      @filter-applied="onFilterApplied"
    />

    <!-- 🌟 Featured Items Carousel Mobile -->
    <section 
      v-if="featuredEquipments.length > 0" 
      class="featured-equipments mb-4 py-4 bg-theme-bg2 border border-theme-border rounded-lg shadow-theme-card relative"
    >
      <h2 class="text-lg font-extrabold text-secondary-500 mb-3 text-center flex items-center justify-center">
        <i class="pi pi-star-fill text-secondary-500 mr-2"></i> Featured Items
      </h2>

      <div class="relative">
        <div class="overflow-hidden">
          <div 
            class="flex transition-transform duration-700 ease-in-out"
            :style="{ transform: `translateX(-${currentFeaturedIndex * 100}%)` }"
          >
            <div 
              v-for="item in featuredEquipments" 
              :key="item.id" 
              class="flex-shrink-0 w-1/2 px-2"
              @click="goToDetail(item.id)"
            >
              <EquipmentCard :item="item" />
            </div>
          </div>
        </div>

        <!-- Prev Button -->
        <button @click="prevFeatured" class="carousel-button left-1">
          <i class="pi pi-chevron-left text-sm"></i>
        </button>

        <!-- Next Button -->
        <button @click="nextFeatured" class="carousel-button right-1">
          <i class="pi pi-chevron-right text-sm"></i>
        </button>
      </div>
    </section>

    <!-- Default Items -->
    <Card />
  </div>

  <!-- Search Results Modal for filters -->
  <SearchResultsModal
    :show="showFilterModal"
    :results="filterResults"
    :isLoading="isFilterLoading"
    @close="closeFilterModal"
  />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useEquipmentsStore } from '@/store/equipments.js';

import Hero from '@/components/Hero.vue';
import Breadcrumb from '@/components/Breadcrumb.vue';
import Filter from '@/components/Filter.vue';
import Card from '@/components/Card.vue';
import EquipmentCard from '@/components/EquipmentCard.vue';
import MobileFilter from '@/components/MobileFilter.vue';
import SearchResultsModal from '@/components/SearchResultsModal.vue';

const store = useEquipmentsStore();
const router = useRouter();

// Search results modal state
const showFilterModal = ref(false);
const filterResults = ref([]);
const isFilterLoading = ref(false);

const onFilterApplied = async () => {
  showFilterModal.value = true;
  isFilterLoading.value = true;
  try {
    // Wait a tick for the store to finish updating from the filter's fetchFilteredEquipments
    await new Promise(r => setTimeout(r, 50));
    // Grab whatever the store now has
    filterResults.value = [...store.equipments];
  } finally {
    isFilterLoading.value = false;
  }
};

// Watch store equipments so modal results stay in sync
watch(() => store.equipments, (newVal) => {
  if (showFilterModal.value) {
    filterResults.value = [...newVal];
    isFilterLoading.value = false;
  }
}, { deep: true });

const closeFilterModal = () => {
  showFilterModal.value = false;
};

// Computed store state
const categories = computed(() => store.categories);
const searchQuery = computed(() => store.searchQuery);

// ✅ Featured Items
const featuredEquipments = computed(() =>
  store.equipments.filter(equipment => equipment.is_featured)
);

// Local refs for filters
const selectedCategories = ref({});
const selectedCities = ref({});

// Cities from equipments
const cities = computed(() => {
  return [...new Set(store.equipments.map(e => e.address?.city).filter(Boolean))];
});

// Featured carousel state
const currentFeaturedIndex = ref(0);

const itemsPerSlide = computed(() => {
  if (window.innerWidth < 640) return 2;
  if (window.innerWidth < 768) return 2;
  if (window.innerWidth < 1024) return 3;
  return 4;
});

// Navigation
const nextFeatured = () => {
  if (currentFeaturedIndex.value < Math.ceil(featuredEquipments.value.length / itemsPerSlide.value) - 1) {
    currentFeaturedIndex.value++;
  } else {
    currentFeaturedIndex.value = 0;
  }
};
const prevFeatured = () => {
  if (currentFeaturedIndex.value > 0) {
    currentFeaturedIndex.value--;
  } else {
    currentFeaturedIndex.value = Math.ceil(featuredEquipments.value.length / itemsPerSlide.value) - 1;
  }
};

// Auto-slide carousel
let featuredInterval = null;
onMounted(async () => {
  featuredInterval = setInterval(() => {
    nextFeatured();
  }, 5000);

  store.pageSize = 40;
  await store.fetchEquipments();   // ensure equipments load for Card & Featured
  await store.fetchCategories();
  await store.fetchCities();
  store.applyFilters();
});

onUnmounted(() => {
  if (featuredInterval) clearInterval(featuredInterval);
});

// Handlers
const updateSelectedCategories = (newSelection) => {
  selectedCategories.value = newSelection;
  triggerFilters();
};
const updateSelectedCities = (newSelection) => {
  selectedCities.value = newSelection;
  triggerFilters();
};
const triggerFilters = () => {
  const filters = {
    search: searchQuery.value || '',
    categories: Object.keys(selectedCategories.value).filter(key => selectedCategories.value[key]),
    cities: Object.keys(selectedCities.value).filter(key => selectedCities.value[key]),
  };
  store.fetchFilteredEquipments(filters);
};

// Navigation to detail
const goToDetail = (equipmentId) => {
  router.push({ name: 'equipment-details', params: { id: equipmentId } });
};
</script>

<style scoped>
.carousel-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: white;
  color: var(--color-secondary-500, #ffc107);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
  z-index: 20;
}

.carousel-button:hover {
  background: var(--color-secondary-500, #ffc107);
  color: var(--color-neutral-900, #1c1c1c);
  transform: translateY(-50%) scale(1.1);
}
</style>
